import * as React from 'react'
import { Image, Pressable, StyleSheet, View } from 'react-native'
import { Icon, Modal, Portal } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import QRCode from 'qrcode'
import * as Sentry from '@sentry/react-native'
import * as Clipboard from 'expo-clipboard'
import Toast from 'react-native-root-toast'
import { TargetType } from '../../../../generated/graphql'
import { Env } from '../../../../env'
import { TouchableIconWithToolTip } from '../../../../utils/meldd-tooltip'

export const EAGLE_VIEW_PARAM = 'eagle-view'

const QR_OPTIONS = { width: 120, margin: 2 }
const QR_OPTIONS_LARGE = { width: 280, margin: 8 }

export type EagleViewShareParams =
  | { targetType: typeof TargetType.Node; targetId: string }
  | { targetType: typeof TargetType.Article; targetId: string }
  | { targetType: typeof TargetType.Cluster; targetId: string; nodeId: string }

function getBaseShareUrlFromTarget(params: EagleViewShareParams): string {
  const base = Env.WEB_APP_URL
  switch (params.targetType) {
    case TargetType.Node:
      return `${base}/ClusterList?nodeId=${params.targetId}`
    case TargetType.Article:
      return `${base}/Perspective?articleId=${params.targetId}`
    case TargetType.Cluster:
      return `${base}/ClusterCards?clusterId=${params.targetId}&nodeId=${params.nodeId}`
    default:
      return ''
  }
}

export function buildEagleViewUrl(baseUrl: string): string {
  if (!baseUrl) return ''
  try {
    const url = new URL(baseUrl)
    url.searchParams.set(EAGLE_VIEW_PARAM, 'true')
    return url.toString()
  } catch {
    const separator = baseUrl.includes('?') ? '&' : '?'
    return `${baseUrl}${separator}${EAGLE_VIEW_PARAM}=true`
  }
}

export function getEagleViewUrl(
  shareUrlOrParams?: string | EagleViewShareParams
): string {
  const baseUrl =
    typeof shareUrlOrParams === 'string'
      ? shareUrlOrParams
      : shareUrlOrParams
      ? getBaseShareUrlFromTarget(shareUrlOrParams)
      : typeof window !== 'undefined'
      ? window.location.href
      : ''
  return buildEagleViewUrl(baseUrl)
}

export function useEagleViewQrDataUrl(eagleViewUrl: string): string | null {
  const [qrDataUrl, setQrDataUrl] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!eagleViewUrl) {
      setQrDataUrl(null)
      return
    }
    QRCode.toDataURL(eagleViewUrl, QR_OPTIONS)
      .then(setQrDataUrl)
      .catch((err) => {
        console.error('QR code generation failed', err)
        Sentry.captureException(err)
        setQrDataUrl(null)
      })
  }, [eagleViewUrl])

  return qrDataUrl
}

export function EagleViewShareCorner({
  shareParams,
}: {
  shareParams?: EagleViewShareParams
}) {
  const { t } = useTranslation(['eagle-view', 'common'])
  const [qrVisible, setQrVisible] = React.useState(true)
  const [bigQrModalVisible, setBigQrModalVisible] = React.useState(false)
  const [bigQrDataUrl, setBigQrDataUrl] = React.useState<string | null>(null)

  const eagleViewUrl = React.useMemo(
    () => getEagleViewUrl(shareParams),
    [shareParams]
  )
  const qrDataUrl = useEagleViewQrDataUrl(eagleViewUrl)

  React.useEffect(() => {
    if (!bigQrModalVisible || !eagleViewUrl) {
      setBigQrDataUrl(null)
      return
    }
    QRCode.toDataURL(eagleViewUrl, QR_OPTIONS_LARGE)
      .then(setBigQrDataUrl)
      .catch((err) => {
        console.error('QR code generation failed', err)
        Sentry.captureException(err)
      })
  }, [bigQrModalVisible, eagleViewUrl])

  const copyShareLink = React.useCallback(async () => {
    if (!eagleViewUrl) return
    try {
      await Clipboard.setStringAsync(eagleViewUrl)
      Toast.show(t('eagle-view:linkCopied'), { position: Toast.positions.TOP })
    } catch (err) {
      console.error('Copy share link failed', err)
      Sentry.captureException(err)
    }
  }, [eagleViewUrl, t])

  const openBigQr = React.useCallback(() => {
    setBigQrModalVisible(true)
  }, [])

  if (!eagleViewUrl) return null

  return (
    <View style={styles.shareCorner}>
      {qrVisible && qrDataUrl ? (
        <Pressable
          onPress={openBigQr}
          accessibilityLabel={t('eagle-view:qrCode')}
          accessibilityHint={t('eagle-view:enlargeQr')}
        >
          <View style={styles.qrContainer}>
            <Image
              source={{ uri: qrDataUrl }}
              style={styles.qrImage}
              accessibilityLabel={t('eagle-view:qrCode')}
            />
          </View>
        </Pressable>
      ) : null}
      <TouchableIconWithToolTip
        tooltip={qrVisible ? t('eagle-view:hideQr') : t('eagle-view:showQr')}
        icon={
          <Icon
            source={qrVisible ? 'eye-off-outline' : 'eye-outline'}
            size={20}
          />
        }
        onPress={() => setQrVisible((v) => !v)}
        mode="text"
      />
      <TouchableIconWithToolTip
        tooltip={t('eagle-view:shareLink')}
        icon={<Icon source="link-variant" size={20} />}
        onPress={copyShareLink}
        mode="text"
      />
      <Portal>
        <Modal
          visible={bigQrModalVisible}
          onDismiss={() => setBigQrModalVisible(false)}
          contentContainerStyle={styles.modalContainer}
        >
          <View style={styles.modalContent}>
            {bigQrDataUrl ? (
              <Image
                source={{ uri: bigQrDataUrl }}
                style={styles.bigQrImage}
                accessibilityLabel={t('eagle-view:qrCode')}
              />
            ) : null}
          </View>
        </Modal>
      </Portal>
    </View>
  )
}

const styles = StyleSheet.create({
  shareCorner: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    alignItems: 'center',
    gap: 8,
  },
  qrContainer: {
    padding: 4,
    backgroundColor: '#fff',
    borderRadius: 4,
  },
  qrImage: {
    width: 56,
    height: 56,
  },
  modalContainer: {
    margin: 24,
    alignSelf: 'center',
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
  },
  modalContent: {
    alignItems: 'center',
  },
  bigQrImage: {
    width: 280,
    height: 280,
  },
})
